import {
  BODY_HEIGHT_CODING,
  BODY_MASS_INDEX_CODING,
  BODY_WEIGHT_CODING,
  DIASTOLIC_BLOOD_PRESSURE_CODING,
  loincCoding,
  loincCodingOf,
  SYSTOLIC_BLOOD_PRESSURE_CODING,
} from '#src/models/loinc.ts'
import { NUTRITION_DERIVATIONS } from '#src/observation-logic/derivations/nutrition.ts'
import { ORTHOSTATIC_DERIVATIONS } from '#src/observation-logic/derivations/orthostatic.ts'
import {
  calculationMethod,
  type DerivationInputs,
  type DerivationRequirement,
  type DerivationRule,
} from '#src/observation-logic/derivations/rules.ts'
import { roundConverted } from '#src/observation-logic/units.ts'
import type { ObservationValue } from '#src/observation-logic/value.ts'

export const WEIGHT_CODING = loincCodingOf(BODY_WEIGHT_CODING)
export const HEIGHT_CODING = loincCodingOf(BODY_HEIGHT_CODING)
export const BMI_CODING = loincCodingOf(BODY_MASS_INDEX_CODING)
export const SYSTOLIC_CODING = loincCodingOf(SYSTOLIC_BLOOD_PRESSURE_CODING)
export const DIASTOLIC_CODING = loincCodingOf(DIASTOLIC_BLOOD_PRESSURE_CODING)
const MAP_CODING = loincCoding('8478-0', 'Mean blood pressure')
export const BSA_CODING = loincCoding('8277-6', 'Body surface area')
export const CREATININE_CODING = loincCoding(
  '2160-0',
  'Creatinine [Mass/volume] in Serum or Plasma'
)
export const EGFR_CODING = loincCoding(
  '62238-1',
  'GFR/1.73 sq M.predicted [Volume Rate/Area] in Serum, Plasma or Blood by Creatinine-based formula (CKD-EPI)'
)
const EGFR_2021_CODING = loincCoding(
  '98979-8',
  'GFR/1.73 sq M.predicted [Volume Rate/Area] in Serum, Plasma or Blood by Creatinine-based formula (CKD-EPI 2021)'
)

const CALCULATION_METHODS = {
  bmiStandard: calculationMethod('bmi-standard', 'BMI (weight / height²)', {
    formula: 'weight_kg / height_m²',
  }),
  mapStandard: calculationMethod(
    'map-standard',
    'MAP (diastolic + ⅓ pulse pressure)',
    { formula: 'diastolic + (systolic − diastolic) / 3' }
  ),
  bsaMosteller: calculationMethod('bsa-mosteller', 'Mosteller', {
    formula: '√(height_cm × weight_kg / 3600)',
    citation: 'Mosteller RD. N Engl J Med 1987;317:1098.',
  }),
  bsaDuBois: calculationMethod('bsa-dubois', 'Du Bois', {
    formula: '0.007184 × weight_kg^0.425 × height_cm^0.725',
    citation: 'Du Bois D, Du Bois EF. Arch Intern Med 1916;17:863–71.',
  }),
  egfrCkdEpi2021: calculationMethod('egfr-ckd-epi-2021', 'CKD-EPI 2021', {
    formula:
      '142 × min(Scr/κ, 1)^α × max(Scr/κ, 1)^−1.200 × 0.9938^age × 1.012 [if female]; κ = 0.7 (F) / 0.9 (M), α = −0.241 (F) / −0.302 (M)',
    citation:
      'Inker LA et al. N Engl J Med 2021;385:1737–49 (race-free creatinine equation); KDIGO 2024.',
    version: '2021',
  }),
  egfrCkdEpi2009: calculationMethod('egfr-ckd-epi-2009', 'CKD-EPI 2009', {
    formula:
      '141 × min(Scr/κ, 1)^α × max(Scr/κ, 1)^−1.209 × 0.993^age × 1.018 [if female]; κ = 0.7 (F) / 0.9 (M), α = −0.329 (F) / −0.411 (M); race coefficient not applied',
    citation: 'Levey AS et al. Ann Intern Med 2009;150:604–12.',
    version: '2009',
  }),
} as const

function quantity(value: number, unit: string): ObservationValue {
  return { kind: 'number', value, unit }
}

export const BMI_RULE: DerivationRule = {
  id: 'bmi',
  target: BMI_CODING,
  inputs: [WEIGHT_CODING, HEIGHT_CODING],
  method: CALCULATION_METHODS.bmiStandard,
  category: 'vital-signs',
  compute: (inputs) => {
    const weight = inputs.quantity(WEIGHT_CODING, 'kg')
    const height = inputs.quantity(HEIGHT_CODING, 'm')
    if (weight === null || height === null || weight <= 0 || height <= 0) {
      return null
    }
    return quantity(roundConverted(weight / height ** 2, 1), 'kg/m2')
  },
}

export const MAP_RULE: DerivationRule = {
  id: 'mean-arterial-pressure',
  target: MAP_CODING,
  inputs: [SYSTOLIC_CODING, DIASTOLIC_CODING],
  method: CALCULATION_METHODS.mapStandard,
  category: 'vital-signs',
  compute: (inputs) => {
    const systolic = inputs.quantity(SYSTOLIC_CODING, 'mm[Hg]')
    const diastolic = inputs.quantity(DIASTOLIC_CODING, 'mm[Hg]')
    if (systolic === null || diastolic === null) {
      return null
    }
    return quantity(
      roundConverted(diastolic + (systolic - diastolic) / 3, 0),
      'mm[Hg]'
    )
  },
}

function bsaRule(
  id: string,
  method: DerivationRule['method'],
  formula: (weightKg: number, heightCm: number) => number
): DerivationRule {
  return {
    id,
    target: BSA_CODING,
    inputs: [WEIGHT_CODING, HEIGHT_CODING],
    method,
    category: 'vital-signs',
    compute: (inputs) => {
      const weight = inputs.quantity(WEIGHT_CODING, 'kg')
      const height = inputs.quantity(HEIGHT_CODING, 'cm')
      if (weight === null || height === null || weight <= 0 || height <= 0) {
        return null
      }
      return quantity(roundConverted(formula(weight, height), 2), 'm2')
    },
  }
}

export const BSA_MOSTELLER_RULE = bsaRule(
  'bsa-mosteller',
  CALCULATION_METHODS.bsaMosteller,
  (weight, height) => Math.sqrt((height * weight) / 3600)
)

export const BSA_DUBOIS_RULE = bsaRule(
  'bsa-dubois',
  CALCULATION_METHODS.bsaDuBois,
  (weight, height) => 0.007184 * weight ** 0.425 * height ** 0.725
)

interface CkdEpiCoefficients {
  intercept: number
  kappa: { female: number; male: number }
  alpha: { female: number; male: number }
  maxExp: number
  ageBase: number
  femaleFactor: number
}

const CKD_EPI_2021: CkdEpiCoefficients = {
  intercept: 142,
  kappa: { female: 0.7, male: 0.9 },
  alpha: { female: -0.241, male: -0.302 },
  maxExp: -1.2,
  ageBase: 0.9938,
  femaleFactor: 1.012,
}

// The 2009 equation without its race coefficient, offered for comparison
// with results reported under it.
const CKD_EPI_2009: CkdEpiCoefficients = {
  intercept: 141,
  kappa: { female: 0.7, male: 0.9 },
  alpha: { female: -0.329, male: -0.411 },
  maxExp: -1.209,
  ageBase: 0.993,
  femaleFactor: 1.018,
}

// CKD-EPI has coefficients for male and female only.
const CKD_EPI_REQUIREMENTS: readonly DerivationRequirement[] = [
  { binding: 'participant.sex', codes: ['male', 'female'] },
  { binding: 'participant.age-years' },
]

// CKD-EPI 2021, creatinine-based and race-free. Null without creatinine,
// a binary sex or an age.
function ckdEpi2021(inputs: DerivationInputs): ObservationValue | null {
  return ckdEpi(inputs, CKD_EPI_2021)
}

function ckdEpi2009(inputs: DerivationInputs): ObservationValue | null {
  return ckdEpi(inputs, CKD_EPI_2009)
}

function ckdEpi(
  inputs: DerivationInputs,
  c: CkdEpiCoefficients
): ObservationValue | null {
  const creatinine = inputs.quantity(CREATININE_CODING, 'mg/dL')
  const sexValue = inputs.variable('participant.sex')
  const sex = sexValue?.kind === 'coding' ? sexValue.value[0]?.code : undefined
  const age = inputs.variable('participant.age-years')
  const ageYears = age?.kind === 'number' ? age.value : null
  if (
    creatinine === null ||
    creatinine <= 0 ||
    (sex !== 'male' && sex !== 'female') ||
    ageYears === null ||
    ageYears < 0
  ) {
    return null
  }
  const ratio = creatinine / c.kappa[sex]
  const gfr =
    c.intercept *
    Math.min(ratio, 1) ** c.alpha[sex] *
    Math.max(ratio, 1) ** c.maxExp *
    c.ageBase ** ageYears *
    (sex === 'female' ? c.femaleFactor : 1)
  return quantity(roundConverted(gfr, 0), 'mL/min/{1.73_m2}')
}

export const EGFR_CKD_EPI_2021_RULE: DerivationRule = {
  id: 'egfr-ckd-epi-2021',
  target: EGFR_CODING,
  inputs: [CREATININE_CODING],
  requires: CKD_EPI_REQUIREMENTS,
  method: CALCULATION_METHODS.egfrCkdEpi2021,
  category: 'laboratory',
  compute: ckdEpi2021,
}

export const EGFR_CKD_EPI_2009_RULE: DerivationRule = {
  id: 'egfr-ckd-epi-2009',
  target: EGFR_CODING,
  inputs: [CREATININE_CODING],
  requires: CKD_EPI_REQUIREMENTS,
  method: CALCULATION_METHODS.egfrCkdEpi2009,
  category: 'laboratory',
  compute: ckdEpi2009,
}

// The same equation for the code that names CKD-EPI 2021 itself.
const EGFR_2021_LOINC_RULE: DerivationRule = {
  ...EGFR_CKD_EPI_2021_RULE,
  id: 'egfr-ckd-epi-2021:98979-8',
  target: EGFR_2021_CODING,
}

// In the order offered: the first rule for a code is its default.
export const STARTER_DERIVATIONS: readonly DerivationRule[] = [
  BMI_RULE,
  MAP_RULE,
  BSA_MOSTELLER_RULE,
  BSA_DUBOIS_RULE,
  EGFR_CKD_EPI_2021_RULE,
  EGFR_CKD_EPI_2009_RULE,
  EGFR_2021_LOINC_RULE,
  ...NUTRITION_DERIVATIONS,
  ...ORTHOSTATIC_DERIVATIONS,
]
